/**
 * TAJWEED TEACHERS (mu'allimūn) — the specific classical masters used for
 * LEARNING tajweed and ahkam al-tilawa. These are prioritised in the reader
 * and featured on the Academy hub.
 *
 * `role: "teacher"` → shown in the "Tajweed Teachers" optgroup + hub cards.
 * `role: "reciter"` → shown in the "Full listening library" optgroup.
 *
 * URL pattern for everyayah.com:
 *   https://everyayah.com/data/{id}/{surah_pad3}{ayah_pad3}.mp3
 */

export const TEACHERS = [
  {
    id: "Husary_128kbps",
    name: "Mahmoud Khalil Al-Husary",
    arabic: "محمود خليل الحصري",
    country: "Egypt",
    role: "teacher",
    style: "Murattal",
    bio: "The reference for teaching tajweed worldwide — Sheikh al-Maqāri' of Egypt and the first qārī to record the entire Qur'an with strict adherence to the rules of tilāwa.",
  },
  {
    id: "Husary_Muallim_128kbps",
    name: "Al-Husary (Mu'allim — teacher edition)",
    arabic: "محمود خليل الحصري (المعلم)",
    country: "Egypt",
    role: "teacher",
    style: "Mu'allim (repeat-after-me)",
    bio: "The famous 'Mu'allim' recording where Sheikh Al-Husary recites and pauses so students can repeat after him — the gold standard for beginners.",
  },
  {
    id: "Minshawy_Murattal_128kbps",
    name: "Muhammad Siddiq Al-Minshawi",
    arabic: "محمد صديق المنشاوي",
    country: "Egypt",
    role: "teacher",
    style: "Murattal",
    bio: "Renowned for the clarity of his makhraj (points of articulation) — his murattal recording is used in classrooms across the Muslim world.",
  },
  {
    id: "Minshawy_Mujawwad_192kbps",
    name: "Al-Minshawi (Mujawwad)",
    arabic: "محمد صديق المنشاوي (مجود)",
    country: "Egypt",
    role: "teacher",
    style: "Mujawwad",
    bio: "The melodic mujawwad recording of Sheikh Al-Minshawi, essential listening for anyone learning the maqāmāt of Qur'anic recitation.",
  },
  {
    id: "Muhammad_Jibreel_128kbps",
    name: "Muhammad Jibreel",
    arabic: "محمد جبريل",
    country: "Egypt",
    role: "teacher",
    style: "Murattal",
    bio: "Imam of Amr ibn al-'As Mosque in Cairo — beloved for his emotionally moving recitation and pristine tajweed.",
  },
  {
    id: "AbdulSamad_64kbps_QuranExplorer.Com",
    name: "Abdul Basit Abdul Samad",
    arabic: "عبد الباسط عبد الصمد",
    country: "Egypt",
    role: "teacher",
    style: "Mujawwad",
    bio: "The 'Golden Throat' — the first qārī to be officially awarded the title of 'Sayyid al-Qurrā'' by the Muslim world. A master of both mujawwad and murattal.",
  },
  {
    id: "Muhammad_Ayyoub_128kbps",
    name: "Muhammad Ayyub",
    arabic: "محمد أيوب",
    country: "Saudi Arabia",
    role: "teacher",
    style: "Murattal",
    bio: "Former imam of the Prophet's Mosque in Madinah — his slow, precise murattal is a favourite of memorisation students.",
  },
  {
    id: "Mahmoud_Ali_Al_Banna_32kbps",
    name: "Mahmoud Ali Al-Banna",
    arabic: "محمود علي البنا",
    country: "Egypt",
    role: "teacher",
    style: "Murattal",
    bio: "One of the great classical qurrā' of Egypt (1926–1985) — celebrated for the meticulous accuracy of his Murattal and used as a verification standard by memorisation teachers.",
  },
];

export const LISTENING_ONLY = [
  { id: "Alafasy_128kbps",                      name: "Mishary Rashid Alafasy",     country: "Kuwait",        source: "everyayah" },
  { id: "Abdurrahmaan_As-Sudais_192kbps",       name: "Abdurrahman As-Sudais",      country: "Saudi Arabia",  source: "everyayah" },
  { id: "Maher_AlMuaiqly_64kbps",               name: "Maher Al-Muaiqly",           country: "Saudi Arabia",  source: "everyayah" },
  { id: "MaherAlMuaiqly128kbps",                name: "Maher Al-Muaiqly (HD)",      country: "Saudi Arabia",  source: "everyayah" },
  { id: "Saood_ash-Shuraym_128kbps",            name: "Saud Al-Shuraim",            country: "Saudi Arabia",  source: "everyayah" },
  { id: "Hudhaify_128kbps",                     name: "Ali Al-Hudhaifi",            country: "Saudi Arabia",  source: "everyayah" },
  { id: "Ahmed_ibn_Ali_al_Ajamy_128kbps",       name: "Ahmed ibn Ali Al-Ajamy",     country: "Saudi Arabia",  source: "everyayah" },
  { id: "Ghamadi_40kbps",                       name: "Saad Al-Ghamdi",             country: "Saudi Arabia",  source: "everyayah" },
  { id: "Abdullaah_3awwaad_Al-Juhaynee_128kbps",name: "Abdullah Al-Juhany",         country: "Saudi Arabia",  source: "everyayah" },
  { id: "Salah_Al_Budair_128kbps",              name: "Salah Al-Budair",            country: "Saudi Arabia",  source: "everyayah" },
  { id: "Abdullah_Basfar_192kbps",              name: "Abdullah Basfar",            country: "Saudi Arabia",  source: "everyayah" },
  { id: "Yasser_Ad-Dussary_128kbps",            name: "Yasser Al-Dosari",           country: "Saudi Arabia",  source: "everyayah" },
  { id: "Nasser_Alqatami_128kbps",              name: "Nasser Al-Qatami",           country: "Saudi Arabia",  source: "everyayah" },
  { id: "Khalefa_Al_Tunaiji_64kbps",            name: "Khalifa Al-Tunaiji",         country: "UAE",           source: "everyayah" },
  // Added by explicit user request. These three are surah-by-surah recordings
  // (per-ayah audio is not available). The reader will play the full surah
  // from the beginning when these reciters are selected.
  { id: "muhammad_alluhaidan",                  name: "Muhammad Al-Luhaidan",       country: "Saudi Arabia",  source: "mp3quran", server: "https://server8.mp3quran.net/lhdan" },
  { id: "abdulaziz_azzahrani",                  name: "Abdul Aziz Al-Zahrani",      country: "Saudi Arabia",  source: "mp3quran", server: "https://server9.mp3quran.net/zahrani" },
  { id: "haitham_addukhayn",                    name: "Haitham Ad-Dukhayn",         country: "Saudi Arabia",  source: "mp3quran", server: "https://server16.mp3quran.net/h_dukhain/Rewayat-Hafs-A-n-Assem" },
];

// Backward compatible flat list used elsewhere (kept in memorised order:
// TEACHERS first, then listening-only).
export const RECITERS = [
  ...TEACHERS.map((t) => ({ id: t.id, name: t.name, country: t.country, source: "everyayah" })),
  ...LISTENING_ONLY,
];

// Build the per-ayah audio URL for any reciter, dispatching on `source`.
// - `everyayah`  → per-ayah audio (e.g. https://everyayah.com/data/Husary_128kbps/001001.mp3)
// - `mp3quran`   → surah-by-surah audio; ayah number is ignored and the full surah is played
export const audioUrl = (reciter, surah, ayah) => {
  const r = typeof reciter === "string"
    ? RECITERS.find((x) => x.id === reciter) || { id: reciter, source: "everyayah" }
    : reciter;
  const s = String(surah).padStart(3, "0");
  const a = String(ayah).padStart(3, "0");
  if (r.source === "mp3quran") {
    return `${r.server}/${s}.mp3`;
  }
  return `https://everyayah.com/data/${r.id}/${s}${a}.mp3`;
};

// Convenience — is the reciter a full-surah-only recording (no per-ayah splits)?
export const isFullSurahOnly = (reciterId) => {
  const r = RECITERS.find((x) => x.id === reciterId);
  return r?.source === "mp3quran";
};
